import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import featureGatesData from '../data/featureGates.json';

// Define feature categories by week of availability
export const FEATURE_GATES = featureGatesData.FEATURE_GATES;

interface FeatureGateContextType {
  isFeatureEnabled: (featureId: string) => boolean;
  getAvailableFeatures: () => string[];
  getUpcomingFeatures: () => { feature: string; daysUntilUnlock: number }[];
  getWeekNumber: () => number;
  newlyUnlockedFeatures: string[];
  dismissNewFeature: (featureId: string) => void;
  accelerateUnlocks: () => void;
  hasSeenFeatureTour: (featureId: string) => boolean;
  markFeatureTourSeen: (featureId: string) => void;
  checkAndTriggerUnlockNotifications: () => void;
}

const FeatureGateContext = createContext<FeatureGateContextType | undefined>(undefined);

export const useFeatureGate = () => {
  const context = useContext(FeatureGateContext);
  if (context === undefined) {
    throw new Error('useFeatureGate must be used within a FeatureGateProvider');
  }
  return context;
};

export const FeatureGateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [userJoinDate, setUserJoinDate] = useState<Date | null>(null);
  const [newlyUnlockedFeatures, setNewlyUnlockedFeatures] = useState<string[]>([]);
  const [seenFeatureTours, setSeenFeatureTours] = useState<Record<string, boolean>>({});
  const [accelerated, setAccelerated] = useState(false);
  const [hasCheckedInitialFeatures, setHasCheckedInitialFeatures] = useState(false);

  useEffect(() => {
    if (user) {
      // Get user join date from profile or use current date as fallback
      const joinDate = user.profile?.created_at
        ? new Date(user.profile.created_at)
        : new Date();

      setUserJoinDate(joinDate);

      // Load previously seen feature tours from localStorage
      const storedTours = localStorage.getItem(`feature_tours_${user.id}`);
      if (storedTours) {
        setSeenFeatureTours(JSON.parse(storedTours));
      }

      // Load accelerated status from localStorage
      const isAccelerated = localStorage.getItem(`accelerated_features_${user.id}`) === 'true';
      setAccelerated(isAccelerated);

      // Initialize seen features for new users with Week 1 features
      // This prevents showing "unlock" notifications for default features
      const seenFeaturesKey = `seen_features_${user.id}`;
      const seenFeatures = localStorage.getItem(seenFeaturesKey);

      if (!seenFeatures) {
        // Mark Week 1 features as already seen for new users
        localStorage.setItem(seenFeaturesKey, JSON.stringify(FEATURE_GATES.WEEK_1));
      }

      // Mark that we've completed initial setup
      // Don't automatically check for new features on login to avoid spam
      setHasCheckedInitialFeatures(true);
    }
  }, [user]);

  // Save seen feature tours to localStorage when updated
  useEffect(() => {
    if (user && Object.keys(seenFeatureTours).length > 0) {
      localStorage.setItem(`feature_tours_${user.id}`, JSON.stringify(seenFeatureTours));
    }
  }, [seenFeatureTours, user]);

  const checkForNewlyUnlockedFeatures = (joinDate: Date, isAccelerated: boolean) => {
    if (!user) return;

    // Get current week number
    const weekNumber = getWeekNumberFromDate(joinDate, isAccelerated);

    // Get available features for this week
    const availableFeatures = getAllFeaturesUpToWeek(weekNumber);

    // Get previously seen features from localStorage
    const seenFeaturesKey = `seen_features_${user.id}`;
    const seenFeatures = JSON.parse(localStorage.getItem(seenFeaturesKey) || '[]');

    // Get last checked week from localStorage
    const lastCheckedWeekKey = `last_checked_week_${user.id}`;
    const lastCheckedWeek = parseInt(localStorage.getItem(lastCheckedWeekKey) || '1');

    // Only show notifications for features from weeks AFTER the last checked week
    // This prevents showing notifications for features the user already had access to
    let newFeatures: string[] = [];

    if (weekNumber > lastCheckedWeek) {
      // User has progressed to a new week
      // Get features from only the new weeks
      const newWeekFeatures: string[] = [];

      if (weekNumber >= 2 && lastCheckedWeek < 2) {
        newWeekFeatures.push(...FEATURE_GATES.WEEK_2);
      }
      if (weekNumber >= 3 && lastCheckedWeek < 3) {
        newWeekFeatures.push(...FEATURE_GATES.WEEK_3);
      }
      if (weekNumber >= 4 && lastCheckedWeek < 4) {
        newWeekFeatures.push(...FEATURE_GATES.WEEK_4);
      }

      // Filter out features that have already been seen
      newFeatures = newWeekFeatures.filter(feature => !seenFeatures.includes(feature));

      // Update last checked week
      localStorage.setItem(lastCheckedWeekKey, weekNumber.toString());
    }

    if (newFeatures.length > 0) {
      setNewlyUnlockedFeatures(newFeatures);
    }
  };

  const getWeekNumberFromDate = (joinDate: Date, isAccelerated: boolean): number => {
    if (!joinDate) return 1;
    
    // If accelerated, return max week
    if (isAccelerated) return 4;
    
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - joinDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const weekNumber = Math.ceil(diffDays / 7);
    
    return Math.min(Math.max(weekNumber, 1), 4); // Clamp between 1-4
  };

  const getWeekNumber = (): number => {
    // Admin users should see all features (week 4)
    if (user?.isAdmin) {
      return 4;
    }
    
    if (!userJoinDate) return 1;
    return getWeekNumberFromDate(userJoinDate, accelerated);
  };

  const getAllFeaturesUpToWeek = (weekNumber: number): string[] => {
    let features: string[] = [];
    
    if (weekNumber >= 1) features = [...features, ...FEATURE_GATES.WEEK_1];
    if (weekNumber >= 2) features = [...features, ...FEATURE_GATES.WEEK_2];
    if (weekNumber >= 3) features = [...features, ...FEATURE_GATES.WEEK_3];
    if (weekNumber >= 4) features = [...features, ...FEATURE_GATES.WEEK_4];
    
    return features;
  };

  const isFeatureEnabled = (featureId: string): boolean => {
    // Admin users should have access to all features
    if (user?.isAdmin) {
      console.log(`Admin user ${user.id} has access to feature: ${featureId}`);
      return true;
    }
    
    if (!userJoinDate) return false;
    
    const weekNumber = getWeekNumberFromDate(userJoinDate, accelerated);
    const availableFeatures = getAllFeaturesUpToWeek(weekNumber);
    
    return availableFeatures.includes(featureId);
  };

  const getAvailableFeatures = (): string[] => {
    // Admin users should have access to all features
    if (user?.isAdmin) {
      return [
        ...FEATURE_GATES.WEEK_1,
        ...FEATURE_GATES.WEEK_2,
        ...FEATURE_GATES.WEEK_3,
        ...FEATURE_GATES.WEEK_4
      ];
    }
    
    if (!userJoinDate) return FEATURE_GATES.WEEK_1;
    
    const weekNumber = getWeekNumberFromDate(userJoinDate, accelerated);
    return getAllFeaturesUpToWeek(weekNumber);
  };

  const getUpcomingFeatures = (): { feature: string; daysUntilUnlock: number }[] => {
    // Admin users have access to all features, so no upcoming features
    if (user?.isAdmin) {
      return [];
    }
    
    if (!userJoinDate) return [];
    
    const weekNumber = getWeekNumberFromDate(userJoinDate, accelerated);
    if (weekNumber >= 4) return []; // No more upcoming features
    
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - userJoinDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const daysInCurrentWeek = diffDays % 7;
    const daysUntilNextWeek = 7 - daysInCurrentWeek;
    
    let upcomingFeatures: { feature: string; daysUntilUnlock: number }[] = [];
    
    if (weekNumber === 1) {
      upcomingFeatures = FEATURE_GATES.WEEK_2.map(feature => ({
        feature,
        daysUntilUnlock: daysUntilNextWeek
      }));
    } else if (weekNumber === 2) {
      upcomingFeatures = FEATURE_GATES.WEEK_3.map(feature => ({
        feature,
        daysUntilUnlock: daysUntilNextWeek
      }));
    } else if (weekNumber === 3) {
      upcomingFeatures = FEATURE_GATES.WEEK_4.map(feature => ({
        feature,
        daysUntilUnlock: daysUntilNextWeek
      }));
    }
    
    return upcomingFeatures;
  };

  const dismissNewFeature = (featureId: string) => {
    if (!user) return;
    
    // Update the list of newly unlocked features
    setNewlyUnlockedFeatures(prev => prev.filter(id => id !== featureId));
    
    // Get previously seen features from localStorage
    const seenFeaturesKey = `seen_features_${user.id}`;
    const seenFeatures = JSON.parse(localStorage.getItem(seenFeaturesKey) || '[]');
    
    // Add the dismissed feature to the seen features list
    if (!seenFeatures.includes(featureId)) {
      const updatedSeenFeatures = [...seenFeatures, featureId];
      localStorage.setItem(seenFeaturesKey, JSON.stringify(updatedSeenFeatures));
    }
    
    // Check if there are more features to show
    if (newlyUnlockedFeatures.length > 1) {
      // Move the dismissed feature to the end of the array to show the next feature
      const updatedFeatures = [...newlyUnlockedFeatures.filter(id => id !== featureId)];
      setNewlyUnlockedFeatures(updatedFeatures);
    }
  };

  const accelerateUnlocks = () => {
    setAccelerated(true);
    if (user) {
      localStorage.setItem(`accelerated_features_${user.id}`, 'true');

      // Get current week to determine what's newly unlocked
      const currentWeek = userJoinDate ? getWeekNumberFromDate(userJoinDate, false) : 1;

      // Get previously seen features
      const seenFeaturesKey = `seen_features_${user.id}`;
      const seenFeatures = JSON.parse(localStorage.getItem(seenFeaturesKey) || '[]');

      // Only show features from weeks AFTER the current week
      const newFeatures: string[] = [];

      if (currentWeek < 2) {
        newFeatures.push(...FEATURE_GATES.WEEK_2);
      }
      if (currentWeek < 3) {
        newFeatures.push(...FEATURE_GATES.WEEK_3);
      }
      if (currentWeek < 4) {
        newFeatures.push(...FEATURE_GATES.WEEK_4);
      }

      // Filter out already seen features
      const unseenNewFeatures = newFeatures.filter(feature => !seenFeatures.includes(feature));

      if (unseenNewFeatures.length > 0) {
        setNewlyUnlockedFeatures(unseenNewFeatures);
      }

      // Update last checked week to 4 since we're accelerating to full access
      localStorage.setItem(`last_checked_week_${user.id}`, '4');
    }
  };

  const hasSeenFeatureTour = (featureId: string): boolean => {
    return !!seenFeatureTours[featureId];
  };

  const markFeatureTourSeen = (featureId: string) => {
    setSeenFeatureTours(prev => ({
      ...prev,
      [featureId]: true
    }));
  };

  const checkAndTriggerUnlockNotifications = () => {
    if (!user || !userJoinDate || !hasCheckedInitialFeatures) return;

    const isAccelerated = localStorage.getItem(`accelerated_features_${user.id}`) === 'true';
    checkForNewlyUnlockedFeatures(userJoinDate, isAccelerated);
  };

  const value = {
    isFeatureEnabled,
    getAvailableFeatures,
    getUpcomingFeatures,
    getWeekNumber,
    newlyUnlockedFeatures,
    dismissNewFeature,
    accelerateUnlocks,
    hasSeenFeatureTour,
    markFeatureTourSeen,
    checkAndTriggerUnlockNotifications
  };

  return <FeatureGateContext.Provider value={value}>{children}</FeatureGateContext.Provider>;
};